import { QueryTable } from "@/components/query-table/QueryTable";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { useTranslation } from "react-i18next";
import { useContractorAssignmentsList } from "../hooks/useContractorAssignmentsList";
import { APIService } from "@/services/api";
import { ZodReadonly, ZodObject, ZodRawShape } from "zod";
import { DataTableFeatures } from "@/types/dataTableSettings";
import { ContractorAssignmentsOverrideFilter } from "@/types/contractor";
import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";

interface ContractorAssignmentsListProps<T extends { id: string }> {
  contractorId: string;
  name: string;
  service: APIService<T, unknown>;
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  titleKey: string;
  translationKey?: string;
  pathBuilder?: (row: T) => string;
  tableFeatures?: DataTableFeatures;
  overrideFilter?: ContractorAssignmentsOverrideFilter;
  columnOverrides?: Record<string, ColumnDef<T, number>>;
}

const ContractorAssignmentsList = <T extends { id: string }>({
  contractorId,
  name,
  service,
  schema,
  titleKey,
  translationKey,
  pathBuilder,
  overrideFilter,
  tableFeatures,
  columnOverrides,
}: ContractorAssignmentsListProps<T>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { query, filteredOptions } = useContractorAssignmentsList({
    name,
    service,
    contractorId,
    pathBuilder,
    enabled: isOpen,
    overrideFilter,
  });

  return (
    <CollapsibleSection
      title={t(titleKey)}
      defaultOpen={false}
      scrollIntoViewOnOpen
      onOpenChange={setIsOpen}
    >
      <QueryTable
        schema={schema}
        name={name}
        translationKey={translationKey}
        pathBuilder={pathBuilder}
        query={query}
        options={filteredOptions}
        tableFeatures={tableFeatures}
        columnOverrides={columnOverrides}
      >
        <QueryTable.Menubar>
          <QueryTable.Menubar.Columns />
          <QueryTable.Menubar.Settings />
        </QueryTable.Menubar>
        <DataTableQueryContextProvider value={{ query }}>
          <QueryTable.Content isInDialog>
            <QueryTable.Status />
          </QueryTable.Content>
        </DataTableQueryContextProvider>
      </QueryTable>
    </CollapsibleSection>
  );
};

export default ContractorAssignmentsList;
